import { Chart, Animations } from 'chart.js';
import { getRelativePosition, callback } from 'chart.js/helpers';
import Annotation from 'chartjs-plugin-annotation';

const EVENT_TYPES = {
  touchstart: 'mousedown',
  touchmove: 'mousemove',
  touchend: 'mouseup',
  pointerenter: 'mouseenter',
  pointerdown: 'mousedown',
  pointermove: 'mousemove',
  pointerup: 'mouseup',
  pointerleave: 'mouseout',
  pointerout: 'mouseout'
};

const DRAFT_ID = 'draft';

const MarkerModes = {
  IDLE: 'idle',
  DRAG: 'drag',
  PLACE: 'place',
  ADD: 'add'
};

const chartStates = new WeakMap();

function getState(chart) {
  let state = chartStates.get(chart);
  if (!state) {
    state = {
      mode: 'idle',
      active: null,
      labels: [],
      markers: {},
      elements: [],
      visibleElements: [],
      hovered: [],
      listeners: []
    };
    chartStates.set(chart, state);
  }

  return state;
}

function getAnnotationState(chart) {
  return Annotation._getState(chart);
}

function getDataLabelsState(chart) {
  return chart.$datalabels;
}

function showDataLabels(chart, dataIndex) {
  const state = getDataLabelsState(chart);

  if (state) {
    state._labels.forEach((label) => {
      if (label._model && label._index === dataIndex) {
        label._model.opacity = 1;
        label.$layout._visible = true;
      }
    });
  }
}

function hideDataLabels(chart, dataIndex) {
  const state = getDataLabelsState(chart);

  if (state) {
    state._labels.forEach((label) => {
      if (label._model && label._index === dataIndex) {
        label._model.opacity = 0;
        label.$layout._visible = false;
      }
    });
  }
}

function lookUpIndex(values, value) {
  for (let index = 1; index < values.length; index += 1) {
    if (value < values[index]) {
      return index - 1;
    }
  }

  return values.length - 1;
}

function getEventIndex(chart, event, values) {
  const { left, right } = chart.chartArea;
  const x = Math.min(Math.max(event.x, left), right);
  const scale = chart.scales.x;
  const value = scale.getValueForPixel(x);
  let index = lookUpIndex(values, value);

  if (index !== values.length - 1) {
    index = value > (values[index] + values[index + 1]) / 2 ? index + 1 : index;
  }

  return index;
}

function setElementIndex(chart, element, values, index) {
  const scale = chart.scales.x;
  const value = values[index];
  const x = scale.getPixelForValue(value);
  moveElement(element, x - element.centerX);
}

function moveElement(element, deltaX) {
  element.x += deltaX;
  element.x2 += deltaX;
  element.centerX += deltaX;

  if (element.elements) {
    element.elements.forEach((subElement) => moveElement(subElement, deltaX));
  }
}

function updateMarkers(chart, state, _options, force = false) {
  const annotationState = getAnnotationState(chart);

  if (!annotationState) {
    return;
  }

  state.markers = annotationState.annotations.reduce((acc, annotation) => {
    const marker = state.markers[annotation.id] ?? { id: annotation.id };

    if (marker.value == null || annotation.value !== state.labels[marker.index] || force) {
      marker.index = Number.isNaN(annotation.value) ? -1 : lookUpIndex(state.labels, annotation.value);
      marker.value = annotation.value;
      annotation.value = state.labels[marker.index] ?? annotation.value;
    }

    marker.label = annotation.label?.content ?? '';

    acc[annotation.id] = marker;

    return acc;
  }, {});
}

function updateElements(chart, state, options, mode) {
  const annotationState = getAnnotationState(chart);

  if (!annotationState) {
    return;
  }

  const PointAnnotation = Chart.registry.getElement('pointAnnotation');
  const animations = resolveAnimations(chart, options.animations, mode);

  state.elements = annotationState.elements.map((element) => {
    if (options.marker.display) {
      const markerOptions = resolveElementOptions(chart, element.options, options.marker, PointAnnotation);
      const markerProperties = resolvePointProperties(chart, element, markerOptions);
      const markerElement = new PointAnnotation();
      Object.assign(markerElement, getInitPointProperties(chart, markerProperties));
      element.elements.push(markerElement);
      animations.update(markerElement, markerProperties);
      state.markers[markerOptions.id].element = markerElement;
    }

    return element;
  }, []);

  state.visibleElements = [...annotationState.visibleElements];
}

function resolveAnimations(chart, options, mode) {
  if (mode === 'reset' || mode === 'none' || mode === 'resize') {
    return {
      update: Object.assign
    };
  }

  return new Animations(chart, options);
}

function resolveElementOptions(_chart, annotation, options, elementClass) {
  return {
    id: annotation.id,
    type: annotation.type,
    drawTime: annotation.drawTime,
    ...elementClass.defaults,
    ...options
  };
}

function resolvePointProperties(_chart, properties, options) {
  const { centerX, y } = properties;
  const { id, radius } = options;

  return {
    x: centerX - radius,
    y: y - radius * 2,
    x2: centerX + radius,
    y2: y,
    centerX: centerX,
    centerY: y - radius,
    radius: id === DRAFT_ID ? 0 : radius,
    width: radius * 2,
    height: radius * 2,
    options
  };
}

function getInitPointProperties(_chart, properties) {
  const { centerX, centerY } = properties;

  return {
    x: centerX,
    y: centerY,
    x2: centerX,
    y2: centerY,
    centerX,
    centerY,
    radius: 0,
    width: 0,
    height: 0
  };
}

function addEventListener(chart, target, type, handler, options) {
  const state = getState(chart);
  const nativeHandler = (event) => {
    handler(state, fromNativeEvent(event, chart), options);
  };

  target.addEventListener(type, nativeHandler, { passive: true });

  state.listeners.push({ target, type, handler: nativeHandler });
}

function removeAllEventListeners(chart) {
  const state = getState(chart);

  while (state.listeners.length > 0) {
    const { target, type, handler } = state.listeners.pop();
    target.removeEventListener(type, handler);
  }
}

function fromNativeEvent(event, chart) {
  const type = EVENT_TYPES[event.type] || event.type;
  const { x, y } = getRelativePosition(event, chart);

  return {
    type,
    chart,
    native: event,
    x: x !== undefined ? x : null,
    y: y !== undefined ? y : null
  };
}

function getMarkers(chart) {
  const state = getState(chart);

  return state.markers;
}

function placeMarker(chart, options) {
  if (!getAnnotationState(chart)) {
    return;
  }

  const { onPlaceStart, onPlaceCancel } = options;
  const state = getState(chart);

  if (callback(onPlaceStart, [{ id: DRAFT_ID }, { chart }]) === false) {
    callback(onPlaceCancel, [{ id: DRAFT_ID }, { chart }]);
  } else {
    state.mode = MarkerModes.PLACE;
    chart.options.plugins.annotation.annotations[DRAFT_ID] = {
      type: 'line',
      scaleID: 'x',
      value: state.labels[0],
      borderWidth: 0
    };
    addEventListener(chart, chart.canvas.ownerDocument, 'keydown', handleCancel, options);
    chart.update('none');
  }
}

function resetMarker(chart, options) {
  const state = getState(chart);
  const { onDragCancel, onPlaceCancel } = options;
  const element = state.active;
  const id = element?.options.id;
  const marker = state.markers[id];
  removeAllEventListeners(chart);

  switch (state.mode) {
    case MarkerModes.DRAG:
      callback(onDragCancel, [marker, { chart }]);
      break;
    case MarkerModes.PLACE:
    case MarkerModes.ADD:
      callback(onPlaceCancel, [marker, { chart }]);
      break;
    default:
      break;
  }

  state.mode = MarkerModes.IDLE;
  state.active = null;

  if (chart.options.plugins.annotation.annotations[DRAFT_ID]) {
    delete chart.options.plugins.annotation.annotations[DRAFT_ID];
  }

  chart.update('none');
}

function addMarker(chart, marker) {
  if (!getAnnotationState(chart)) {
    return;
  }

  const state = getState(chart);
  state.mode = MarkerModes.IDLE;
  state.active = null;

  if (chart.options.plugins.annotation.annotations[DRAFT_ID]) {
    delete chart.options.plugins.annotation.annotations[DRAFT_ID];
  }

  chart.options.plugins.annotation.annotations[marker.id] = {
    type: 'line',
    scaleID: 'x',
    value: marker.value
  };
  chart.update('none');
}

function moveMarker(chart, marker) {
  if (!getAnnotationState(chart)) {
    return;
  }

  const state = getState(chart);
  state.mode = MarkerModes.IDLE;
  state.active = null;
  chart.options.plugins.annotation.annotations[marker.id].value = marker.value;
  chart.update('none');
}

function removeMarker(chart, marker) {
  if (!getAnnotationState(chart)) {
    return;
  }

  const state = getState(chart);
  state.mode = MarkerModes.IDLE;
  state.active = null;
  delete chart.options.plugins.annotation.annotations[marker.id];
  chart.update('none');
}

function handleDragStart(state, event, options) {
  const { onDragStart, onDragCancel } = options;
  const chart = event.chart;
  const element = state.visibleElements.findLast(
    (element) => element.elements.length > 1 && element.elements[1].inRange(event.x, event.y)
  );

  if (element) {
    const marker = state.markers[element.options.id];

    if (callback(onDragStart, [marker, event]) === false) {
      callback(onDragCancel, [marker, event]);
    } else {
      state.mode = MarkerModes.DRAG;
      state.active = element;
      addEventListener(chart, chart.canvas, 'pointerup', handleDragEnd, options);
      addEventListener(chart, chart.canvas, 'touchend', handleDragEnd, options);
      addEventListener(chart, chart.canvas.ownerDocument, 'keydown', handleCancel, options);
    }
  }

  return false;
}

function handleDragMove(state, event, options) {
  const { onDrag, onPlace } = options;
  const chart = event.chart;
  const element = state.active;
  const id = element.options.id;
  const marker = state.markers[id];
  const index = getEventIndex(chart, event, state.labels);

  if (marker.index !== index) {
    hideDataLabels(chart, marker.index);
    marker.index = index;
    marker.value = state.labels[index];
    setElementIndex(chart, element, state.labels, index);

    if (state.mode === MarkerModes.DRAG) {
      callback(onDrag, [marker, event]);
    }

    if (state.mode === MarkerModes.PLACE) {
      callback(onPlace, [marker, event]);
    }

    return true;
  }

  return false;
}

function handleDragEnd(state, event, options) {
  const { onDragEnd } = options;
  const chart = event.chart;
  removeAllEventListeners(chart);
  const element = state.active;
  const id = element.options.id;
  const marker = state.markers[id];
  callback(onDragEnd, [marker, event]);
  moveMarker(chart, state.markers[state.active.options.id]);
}

function handleAddStart(state, event, options) {
  const chart = event.chart;
  state.mode = MarkerModes.ADD;
  addEventListener(chart, chart.canvas, 'pointerup', handleAddEnd, options);
  addEventListener(chart, chart.canvas, 'touchend', handleAddEnd, options);

  return false;
}

function handleAddEnd(state, event, options) {
  const { onPlaceEnd } = options;
  const chart = event.chart;
  removeAllEventListeners(chart);
  const marker = {
    ...state.markers[DRAFT_ID],
    id: Math.random().toString(16).slice(2)
  };
  callback(onPlaceEnd, [marker, event]);
  addMarker(chart, marker);
}

function handlePlace(state, event, options) {
  const chart = event.chart;
  const inChartArea = chart.isPointInArea(event);
  const hidden = !state.active.options.borderWidth;
  let changed = handleDragMove(state, event, options);

  if (inChartArea && hidden) {
    state.active.options.borderWidth = 2;
    state.active.elements[1].radius = 12;
    changed = true;
  }

  if (!inChartArea && !hidden) {
    state.active.options.borderWidth = 0;
    state.active.elements[1].radius = 0;
    changed = true;
  }

  return changed;
}

function handleCancel(_, event, options) {
  if (event.native.key !== 'Escape') {
    return;
  }

  resetMarker(event.chart, options);
}

function handleHover(state, event, options) {
  const { onHover } = options;
  const chart = event.chart;
  const index = getEventIndex(chart, event, state.labels);
  const hovered = state.hovered;
  state.hovered = Object.values(state.markers).filter((marker) => marker.index === index);

  if (
    hovered.length !== state.hovered.length ||
    state.hovered.some((marker, index) => hovered[index].id !== marker.id)
  ) {
    return callback(onHover, [state.hovered, event]) || false;
  }

  return false;
}

function handleMouseMove(state, event, options) {
  switch (state.mode) {
    case MarkerModes.DRAG:
      return handleDragMove(state, event, options);
    case MarkerModes.PLACE:
      return handlePlace(state, event, options);
    case MarkerModes.IDLE:
      return handleHover(state, event, options);
    case MarkerModes.ADD:
    default:
      return false;
  }
}

function handleMouseDown(state, event, options) {
  switch (state.mode) {
    case MarkerModes.PLACE:
      return handleAddStart(state, event, options);
    case MarkerModes.IDLE:
      return options.enabled && handleDragStart(state, event, options);
    case MarkerModes.ADD:
    case MarkerModes.DRAG:
    default:
      return false;
  }
}

function handleMouseOut(state, event, options) {
  const { onHover } = options;

  if (state.mode === MarkerModes.IDLE) {
    return callback(onHover, [[], event]) || false;
  }

  return false;
}

function handleEvent(state, event, options) {
  switch (event.type) {
    case 'mousemove':
      return handleMouseMove(state, event, options);
    case 'mousedown':
      return handleMouseDown(state, event, options);
    case 'mouseout':
      return handleMouseOut(state, event, options);
    default:
      return false;
  }
}

const marker = {
  id: 'marker',
  defaults: {
    enabled: false,
    animations: {
      numbers: {
        properties: ['x', 'y', 'x2', 'y2', 'width', 'height', 'centerX', 'centerY', 'pointX', 'pointY', 'radius'],
        type: 'number'
      }
    },
    marker: {
      display: false,
      pointStyle: 'triangle',
      radius: 12,
      rotation: 180
    }
  },
  _getState: getState,
  start: (chart, _args, options) => {
    chart.getMarkers = () => getMarkers(chart, options);
    chart.placeMarker = () => placeMarker(chart, options);
    chart.resetMarker = () => resetMarker(chart, options);
    chart.addMarker = (marker) => addMarker(chart, marker, options);
    chart.moveMarker = (marker) => moveMarker(chart, marker, options);
    chart.removeMarker = (marker) => removeMarker(chart, marker, options);
  },
  beforeUpdate: (chart, _args, options) => {
    const state = getState(chart);
    const shouldUpdateIndexes = state.labels !== chart.data.labels;

    if (shouldUpdateIndexes) {
      state.labels = chart.data.labels;
    }

    updateMarkers(chart, state, options, shouldUpdateIndexes);
  },
  afterUpdate: (chart, args, options) => {
    const state = getState(chart);

    updateElements(chart, state, options, args.mode);

    if (state.mode === MarkerModes.PLACE) {
      state.active = state.elements.find((element) => element.options.id === DRAFT_ID);
    }

    state.visibleElements.forEach((element) => {
      const marker = state.markers[element.options.id];
      const hidden = callback(options.marker.label, [marker, { chart }]) === false;

      if (!hidden && element.options.borderWidth > 0) {
        showDataLabels(chart, state.markers[element.options.id].index);
      }
    });
  },
  afterEvent: (chart, args, options) => {
    const state = getState(chart);

    if (handleEvent(state, args.event, options)) {
      args.changed = true;
    }

    state.visibleElements.forEach((element) => {
      const marker = state.markers[element.options.id];
      const hidden = callback(options.marker.label, [marker, { chart }]) === false;

      if (!hidden && element.options.borderWidth > 0) {
        showDataLabels(chart, state.markers[element.options.id].index);
      }
    });
  }
};

export default marker;
